import { useState } from "react";
import { api, ApiError, toApiError, unwrap, type Schemas } from "../../api/client";
import { ErrorMessage, LiveMessage, Loading } from "../../components/ui";
import { t } from "../../i18n/t";
import { formatDate, formatDateTime } from "../../lib/format";
import { useQuery } from "../../lib/useQuery";

type Lead = Schemas["LeadOut"];

/** Unhandled first, then newest first. */
function ordered(leads: Lead[]): Lead[] {
  return [...leads].sort(
    (a, b) =>
      Number(a.handled_at !== null) - Number(b.handled_at !== null) ||
      b.created_at.localeCompare(a.created_at),
  );
}

/** Contact-form requests ("Bestill gjennomgang"); the operator marks them handled. */
export function LeadsTab() {
  const leads = useQuery("admin:leads", (signal) =>
    unwrap(api.GET("/api/admin/leads", { signal })),
  );
  const [message, setMessage] = useState("");
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function markHandled(lead: Lead) {
    setBusy(lead.id);
    setError(null);
    try {
      const updated = await unwrap(
        api.POST("/api/admin/leads/{lead_id}/handled", {
          params: { path: { lead_id: lead.id } },
        }),
      );
      leads.setData((prev) => prev?.map((l) => (l.id === updated.id ? updated : l)));
      setMessage(t("admin.leadMarked", { name: lead.name }));
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <LiveMessage message={message} />
      {error && <ErrorMessage error={error} />}
      <div className="toolbar">
        <button type="button" className="btn btn-secondary" onClick={leads.reload}>
          {t("admin.refresh")}
        </button>
      </div>
      {leads.error && <ErrorMessage error={leads.error} onRetry={leads.reload} />}
      {!leads.data && !leads.error && <Loading />}
      {leads.data && leads.data.length === 0 && <p>{t("admin.leadsEmpty")}</p>}
      {leads.data && leads.data.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <caption className="visually-hidden">{t("admin.leadsCaption")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("admin.colReceived")}</th>
                <th scope="col">{t("admin.colNavn")}</th>
                <th scope="col">{t("admin.colEmail")}</th>
                <th scope="col">{t("admin.colOrganisation")}</th>
                <th scope="col">{t("admin.colRole")}</th>
                <th scope="col">{t("admin.colKommune")}</th>
                <th scope="col">{t("admin.colMessage")}</th>
                <th scope="col">{t("admin.colStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {ordered(leads.data).map((l) => (
                <tr key={l.id}>
                  <td className="nowrap">{formatDateTime(l.created_at)}</td>
                  <th scope="row">{l.name}</th>
                  <td>
                    <a href={`mailto:${encodeURIComponent(l.email)}`}>{l.email}</a>
                  </td>
                  <td>{l.organisation}</td>
                  <td>{l.role ?? t("common.dash")}</td>
                  <td>{l.kommunenummer ?? t("common.dash")}</td>
                  <td className="lead-message">{l.message ?? t("common.dash")}</td>
                  <td>
                    {l.handled_at ? (
                      t("admin.leadHandled", { date: formatDate(l.handled_at) })
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary btn-small"
                        disabled={busy === l.id}
                        onClick={() => void markHandled(l)}
                        aria-label={t("admin.markHandledLabel", { name: l.name })}
                      >
                        {t("admin.markHandled")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
